import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Building2, Star, Trash2 } from 'lucide-react';
import {
  useCompanies,
  useDeleteCompany,
  useSetDefaultCompany,
  useUpdateCompany,
} from '../hooks/useCompanies';
import type { Company, DeleteCompanyResult } from '../lib/companiesApi';
import {
  CompanyFormFields,
  companyFormToInput,
  companyToForm,
  type CompanyFormState,
} from '../components/settings/CompanyProfileSettings';
import { AdministratorsCard } from '../components/companies/AdministratorsCard';
import { CompanyDocumentsCard } from '../components/companies/CompanyDocumentsCard';
import { EfacturaCard } from '../components/companies/EfacturaCard';
import { usePermissions } from '../hooks/usePermissions';
import { useI18n } from '../contexts/I18nContext';
import { useConfirm } from '../components/ui/ConfirmProvider';

// Company detail page (/settings/companies/:id). Replaces the old edit modal
// from Settings → Firme: each firm has its own page with the identity form
// (incl. ANAF prefill) and the per-company SmartBill configuration.
// Gated by settings.canManageCompanies, same as the Settings tab.

export function CompanyDetailPage() {
  const { t } = useI18n();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canManageCompanies = can('settings', 'canManageCompanies');

  const { data: companies, isLoading, isError } = useCompanies();
  const company = companies?.find((c) => c.id === id) ?? null;

  const setDefault = useSetDefaultCompany();
  const deleteCompany = useDeleteCompany();
  const confirm = useConfirm();

  const [error, setError] = useState<string | null>(null);

  async function handleSetDefault() {
    if (!company) return;
    setError(null);
    try {
      await setDefault.mutateAsync(company.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('companies.error.generic'));
    }
  }

  async function handleDelete() {
    if (!company) return;
    setError(null);
    const name = company.displayName || company.legalName;
    if (!(await confirm({ message: t('companies.deleteConfirm', { name }), danger: true }))) return;
    try {
      const result: DeleteCompanyResult = await deleteCompany.mutateAsync(company.id);
      // Back to the firms list; it shows its own state, so just log the counts.
      console.info('Company deleted', result);
      navigate('/settings/companies');
    } catch (e) {
      setError(e instanceof Error ? e.message : t('companies.error.generic'));
    }
  }

  if (!canManageCompanies) {
    return (
      <div className="mx-auto max-w-3xl p-4 sm:p-6 lg:p-8">
        <p className="text-sm text-gray-500">{t('companies.detail.forbidden')}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-3xl p-4 sm:p-6 lg:p-8">
      <Link
        to="/settings/companies"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-800"
      >
        <ArrowLeft size={15} />
        {t('companies.detail.back')}
      </Link>

      {isLoading && <p className="mt-6 text-sm text-gray-500">{t('companies.loading')}</p>}
      {isError && <p className="mt-6 text-sm text-red-600">{t('companies.error.load')}</p>}
      {!isLoading && !isError && !company && (
        <p className="mt-6 text-sm text-gray-500">{t('companies.detail.notFound')}</p>
      )}

      {company && (
        <>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Building2 size={22} className="shrink-0 text-gray-400" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h1 className="truncate text-2xl font-bold text-gray-900">
                  {company.displayName || company.legalName}
                </h1>
                {company.isDefault && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700">
                    <Star size={11} />
                    {t('companies.defaultBadge')}
                  </span>
                )}
              </div>
              <p className="truncate text-sm text-gray-500">
                {company.displayName ? `${company.legalName} · ` : ''}
                {company.cui || t('companies.noCui')}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {!company.isDefault && (
                <button
                  type="button"
                  onClick={handleSetDefault}
                  disabled={setDefault.isPending}
                  className="ui-btn-secondary disabled:opacity-50"
                >
                  {t('companies.setDefault')}
                </button>
              )}
              {!company.isDefault && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleteCompany.isPending}
                  className="inline-flex items-center gap-1.5 rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                >
                  <Trash2 size={15} />
                  {t('common.delete')}
                </button>
              )}
            </div>
          </div>

          {error && (
            <p className="mt-4 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-600">
              {error}
            </p>
          )}

          <CompanyDataCard company={company} />
          <CompanyDocumentsCard companyId={company.id} canManage={canManageCompanies} />
          <AdministratorsCard companyId={company.id} />
          <EfacturaCard companyId={company.id} />
        </>
      )}
    </div>
  );
}

/** The firm identity/contact form, hydrated from the server row. */
function CompanyDataCard({ company }: { company: Company }) {
  const { t } = useI18n();
  const update = useUpdateCompany();

  const [form, setForm] = useState<CompanyFormState>(() => companyToForm(company));
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-hydrate when the server row changes (e.g. set-default refetch) unless
  // the user has unsaved edits. A ref (not state) so that clearing the dirty
  // flag after save doesn't re-run the effect with the stale cached row.
  const dirtyRef = useRef(false);
  useEffect(() => {
    if (!dirtyRef.current) setForm(companyToForm(company));
  }, [company]);

  function patch(next: Partial<CompanyFormState>) {
    setForm((prev) => ({ ...prev, ...next }));
    dirtyRef.current = true;
    setSaved(false);
  }

  async function handleSave() {
    if (!form.legalName.trim()) {
      setError(t('companies.legalNameRequired'));
      return;
    }
    setError(null);
    try {
      const updated = await update.mutateAsync({
        id: company.id,
        input: companyFormToInput(form),
      });
      setForm(companyToForm(updated));
      dirtyRef.current = false;
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('companies.error.generic'));
    }
  }

  return (
    <div className="mt-6 rounded-lg border border-gray-200 bg-white p-5">
      <h2 className="text-base font-semibold text-gray-900">{t('companies.detail.dataHeading')}</h2>

      <div className="mt-4">
        <CompanyFormFields form={form} update={patch} />
      </div>

      {error && (
        <p className="mt-4 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-600">
          {error}
        </p>
      )}
      {saved && (
        <p className="mt-4 rounded-md border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-700">
          {t('companies.detail.saved')}
        </p>
      )}

      <button
        type="button"
        onClick={handleSave}
        disabled={update.isPending}
        className="ui-btn-primary mt-4 disabled:opacity-60"
      >
        {update.isPending ? t('companies.saving') : t('common.save')}
      </button>
    </div>
  );
}
